import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import API_URL from '../config';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './About.css';

function About() {
  const { t, i18n } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ state: 'idle', error: '' });

  useEffect(() => {
    document.title = 'SmartStays - Over ons';
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!validateRequiredFields(e.target)) {
      setNewsletterStatus({ state: 'error', error: t('common.fillRequired') });
      return;
    }
    setNewsletterStatus({ state: 'loading', error: '' });
    try {
      await axios.post(`${API_URL}/api/newsletter`, { email: newsletterEmail, lang: i18n.language });
      setNewsletterStatus({ state: 'success', error: '' });
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterStatus({
        state: 'error',
        error: err.response?.data?.error || t('about.newsletterError'),
      });
    }
  };

  return (
    <div className="about-container">
      <Navbar />

      {/* Hero */}
      <section className="about-hero">
        <h1>{t('about.title')}</h1>
        <p className="about-tagline">{t('about.tagline')}</p>
      </section>

      {/* Missie/verhaal */}
      <section className="about-section">
        <h2>{t('about.storyTitle')}</h2>
        <p>{t('about.storyP1')}</p>
        <p>{t('about.storyP2')}</p>
      </section>

      {/* Team */}
      <section className="about-section alt-bg">
        <div className="about-team-photos">
          <div className="about-team-member">
            <div className="about-team-frame about-team-frame-thomas"><img src="/team-thomas-wide.jpg" alt="Thomas De Schepper" /></div>
            <p>Thomas De Schepper</p>
            <a href="https://www.linkedin.com/in/thomas-de-schepper-b496632a4/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Thomas De Schepper" className="about-team-linkedin">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="6.5" y="10" width="2.4" height="7.5" fill="currentColor"/>
                <circle cx="7.7" cy="6.8" r="1.4" fill="currentColor"/>
                <path d="M11.5 17.5V10M11.5 12.8C11.5 11.2 12.5 10 13.9 10C15.3 10 16.2 11 16.2 12.8V17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
          <div className="about-team-member">
            <div className="about-team-frame"><img src="/team-tibo.jpg" alt="Tibo Huyghe" /></div>
            <p>Tibo Huyghe</p>
            <a href="https://www.linkedin.com/in/tibo-huyghe-9b1b53224/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Tibo Huyghe" className="about-team-linkedin">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                <rect x="6.5" y="10" width="2.4" height="7.5" fill="currentColor"/>
                <circle cx="7.7" cy="6.8" r="1.4" fill="currentColor"/>
                <path d="M11.5 17.5V10M11.5 12.8C11.5 11.2 12.5 10 13.9 10C15.3 10 16.2 11 16.2 12.8V17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>

        <h2>{t('about.teamTitle')}</h2>
        <p>{t('about.teamText')}</p>
      </section>

      {/* Waarom kiezen voor SmartStays */}
      <section className="about-section">
        <h2>{t('about.whyTitle')}</h2>
        <div className="about-grid">
          <div className="about-card">
            <h3>{t('about.why1Title')}</h3>
            <p>{t('about.why1Text')}</p>
          </div>
          <div className="about-card">
            <h3>{t('about.why2Title')}</h3>
            <p>{t('about.why2Text')}</p>
          </div>
          <div className="about-card">
            <h3>{t('about.why3Title')}</h3>
            <p>{t('about.why3Text')}</p>
          </div>
          <div className="about-card">
            <h3>{t('about.why4Title')}</h3>
            <p>{t('about.why4Text')}</p>
          </div>
        </div>
      </section>

      {/* Socials */}
      <section className="about-section alt-bg">
        <h2>{t('about.socialsTitle')}</h2>
        <p>{t('about.socialsText')}</p>
        <div className="about-socials">
          <a href="https://www.instagram.com/smartstaysbe/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.5"/>
              <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor"/>
            </svg>
          </a>
          <a href="https://www.facebook.com/profile.php?id=61591161593047" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M13.5 21V13.2H15.8L16.2 10.4H13.5V8.6C13.5 7.8 13.7 7.2 14.9 7.2H16.3V4.7C16 4.7 15.1 4.6 14.1 4.6C11.9 4.6 10.4 5.9 10.4 8.3V10.4H8V13.2H10.4V21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
          <a href="https://www.tiktok.com/@smartstaysbe" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3V14.8C14 16.35 12.75 17.6 11.2 17.6C9.65 17.6 8.4 16.35 8.4 14.8C8.4 13.25 9.65 12 11.2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 3C14 5.4 16.2 7.5 18.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
          <a href="https://www.linkedin.com/company/smartstaysbe/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="6.5" y="10" width="2.4" height="7.5" fill="currentColor"/>
              <circle cx="7.7" cy="6.8" r="1.4" fill="currentColor"/>
              <path d="M11.5 17.5V10M11.5 12.8C11.5 11.2 12.5 10 13.9 10C15.3 10 16.2 11 16.2 12.8V17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>

        <div className="about-newsletter">
          <h3>{t('about.newsletterTitle')}</h3>
          <p>{t('about.newsletterText')}</p>
          {newsletterStatus.state === 'success' ? (
            <p className="about-newsletter-success">{t('about.newsletterSuccess')}</p>
          ) : (
            <form className="about-newsletter-form" onSubmit={handleNewsletterSubmit} noValidate onInput={clearFieldInvalid}>
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder={t('about.newsletterPlaceholder')}
                required
              />
              <button type="submit" disabled={newsletterStatus.state === 'loading'}>
                {newsletterStatus.state === 'loading' ? t('common.loading') : t('about.newsletterSubmit')}
              </button>
            </form>
          )}
          {newsletterStatus.state === 'error' && (
            <p className="about-newsletter-error">{newsletterStatus.error}</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <div className="about-cta">
        <h2>{t('about.ctaTitle')}</h2>
        <p>{t('about.ctaText')}</p>
        <Link to="/contact" className="about-cta-btn">{t('about.ctaBtn')}</Link>
      </div>


      <Footer />
    </div>
  );
}

export default About;
